import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProductionStore } from '../../stores/useProductionStore'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'

export const Production: React.FC = () => {
  const { t } = useTranslation()
  const { products, activeProductions, isLoading, loadProducts, startProduction, collectProduction } = useProductionStore()
  const [selectedProduct, setSelectedProduct] = useState<string>('')

  useEffect(() => {
    loadProducts()
  }, [])

  const handleStartProduction = () => {
    if (selectedProduct) {
      startProduction(selectedProduct)
      setSelectedProduct('')
    }
  }

  const getTimeRemaining = (endTime: number) => {
    const remaining = Math.max(0, endTime - Date.now())
    const minutes = Math.floor(remaining / 60000)
    const seconds = Math.floor((remaining % 60000) / 1000)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  if (isLoading) {
    return (
      <Card title={`⚙️ ${t('production.title')}`} subtitle={t('production.loading')}>
        <div className="flex justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Start New Production */}
      <Card title={`⚙️ ${t('production.startNewProduction')}`} subtitle={t('production.startNewProductionSubtitle')}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('production.selectProduct')}
            </label>
            <select
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              className="input-field"
            >
              <option value="">{t('production.selectProduct')}</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} - {product.base_price} {t('common.price')} - {product.production_time} {t('production.seconds')}
                </option>
              ))}
            </select>
          </div>
          
          {selectedProduct && (
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2">اطلاعات تولید</h4>
              {(() => {
                const product = products.find(p => p.id === selectedProduct)
                return product ? (
                  <div className="text-sm text-blue-700">
                    <p>محصول: {product.name}</p>
                    <p>قیمت: {product.base_price} تومان</p>
                    <p>زمان تولید: {product.production_time} ثانیه</p>
                    <p>سطح مورد نیاز: {product.required_level}</p>
                  </div>
                ) : null
              })()}
            </div>
          )}
          
          <Button
            onClick={handleStartProduction}
            disabled={!selectedProduct}
            variant="primary"
            className="w-full"
          >
            {t('production.startProduction')}
          </Button>
        </div>
      </Card>

      {/* Active Productions */}
      <Card title={t('production.activeProductions')} subtitle={t('production.activeProductionsSubtitle')}>
        <div className="space-y-3">
          {activeProductions.length > 0 ? (
            activeProductions.map((production) => {
              const isReady = production.endTime <= Date.now()
              return (
                <div
                  key={production.id}
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    isReady ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ml-3 ${
                      isReady ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      <span className={isReady ? 'text-green-600' : 'text-blue-600'}>
                        {isReady ? '✅' : '⏳'}
                      </span>
                    </div>
                    <div>
                      <h5 className="font-medium">{production.productName}</h5>
                      <p className="text-sm text-gray-600">
                        {t('production.quantity')}: {production.quantity}
                      </p>
                      {!isReady && (
                        <p className="text-xs text-gray-500">
                          {t('production.timeRemaining')}: {getTimeRemaining(production.endTime)}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => collectProduction(production.id)}
                    disabled={!isReady}
                    variant={isReady ? "primary" : "secondary"}
                    size="sm"
                  >
                    {isReady ? t('production.collect') : t('production.waiting')}
                  </Button>
                </div>
              )
            })
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⚙️</span>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                {t('production.noActiveProductions')}
              </h3>
              <p className="text-gray-500">
                {t('production.noActiveProductionsMessage')}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Available Products */}
      <Card title={t('production.availableProducts')} subtitle={t('production.availableProductsSubtitle')}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((product) => (
            <div key={product.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-semibold text-gray-700">{product.name}</h4>
                <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded capitalize">
                  {t(`production.${product.category}`)}
                </span>
              </div>
              <div className="space-y-1 text-sm text-gray-600">
                <p>{t('production.price')}: {product.base_price} {t('common.price')}</p>
                <p>{t('production.productionTime')}: {product.production_time} {t('production.seconds')}</p>
                <p>{t('production.requiredLevel')}: {product.required_level}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
